import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Paper,
    Typography,
    FormGroup,
    FormControlLabel,
    Checkbox,
    TextField,
    Button,
    Alert,
    Snackbar,
    Divider,
    Card,
    CardContent,
    Grid,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Chip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress
} from '@mui/material';
import {
    CheckCircle as CheckCircleIcon,
    Assignment as AssignmentIcon,
    Gavel as GavelIcon,
    Security as SecurityIcon,
    Description as DescriptionIcon,
    CloudUpload as UploadIcon,
    Visibility as ViewIcon,
    Save as SaveIcon,
    Send as SendIcon,
    Warning as WarningIcon,
    GetApp as DownloadIcon,
    Payment as PaymentIcon,
    Lock as LockIcon,
    Error as ErrorIcon
} from '@mui/icons-material';
import FileUploadComponent from '../../../components/FileUploadComponent';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import authService from '../../../services/authService';
import paymentService from '../../../services/paymentService';
import { decryptText, decryptAppId } from '../../../utils/encryption';
import FeesService from '../../../services/feesService';
import declarationService from '../../../services/declarationService';
import TestingLaboratoryService from '../../../services/testinglaboratoryService';
import s3Utils from '../../../utils/s3Utils';
import previewReportService from '../../../services/previewReportService';
import {
    insertDANC,
    updateDANC,
    getDANCById
} from '../../../services/apiCalibration';
import { encryptAppId, encryptText } from '../../../utils/encryption';

// Adequacy Check Component with DA NC Integration
const AdequacyCheckSection = React.memo(({
    sectionId,
    title,
    applicationStage,
    orgId,
    appId,
    adequacyChecks,
    handleAdequacyCheck,
    handleAdequacyRemarks,
    handleAdequacySubmit,
    loading,
    currentUser = 'System User',
    currentUserId = 0
}) => {
    // Local state for DA NC operations
    const [dancLoading, setDancLoading] = useState(false);
    const [dancError, setDancError] = useState('');
    const [dancSuccess, setDancSuccess] = useState('');
    const [existingDANCRecord, setExistingDANCRecord] = useState(null);
    const [dataLoaded, setDataLoaded] = useState(false);

    // Load existing DA NC data on component mount
    useEffect(() => {
        const loadExistingDANCData = async () => {
            if (!orgId || !appId) return;

            try {
                setDancLoading(true);
                const response = await getDANCById(orgId, appId, title);

                if (response.data.success && response.data.data) {
                    setExistingDANCRecord(response.data.data);

                    // Bind DA NC data to adequacy check fields
                    if (response.data.data.InformationStatus) {
                        handleAdequacyCheck(sectionId, response.data.data.InformationStatus);
                    }

                    const remarks = response.data.data.InformationProvided || response.data.data.Observation || '';
                    if (remarks) {
                        handleAdequacyRemarks(sectionId, remarks);
                    }
                }
                setDataLoaded(true);
            } catch (error) {
                console.log('No existing DA NC record found or error loading:', error.message);
                setDataLoaded(true);
            } finally {
                setDancLoading(false);
            }
        };

        if (!dataLoaded) {
            loadExistingDANCData();
        }
    }, [orgId, appId, title, sectionId, dataLoaded, handleAdequacyCheck, handleAdequacyRemarks]);

    // Clear messages after timeout
    useEffect(() => {
        if (dancError || dancSuccess) {
            const timer = setTimeout(() => {
                setDancError('');
                setDancSuccess('');
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [dancError, dancSuccess]);

    // Only show if applicationStage is 18, 18b or 18a
    if (applicationStage !== "18" && applicationStage !== "18a" && applicationStage !== "18b") return null;

    // Check if adequacy checks should be read-only (stage 18a)
    const isAdequacyReadOnly = applicationStage === "18a";

    const adequacy = adequacyChecks[sectionId] || { status: '', remarks: '' };

    // Standard status change handler
    const handleStatusChange = (status) => {
        handleAdequacyCheck(sectionId, status);
    };

    // Standard remarks change handler
    const handleRemarksChange = (remarks) => {
        handleAdequacyRemarks(sectionId, remarks);
    };

    // Enhanced submit handler with DA NC API call
    const handleSubmit = async () => {
        try {
            setDancLoading(true);
            setDancError('');
            setDancSuccess('');

            // First submit the adequacy check
            await handleAdequacySubmit(sectionId);

            // Then create/update DA NC record based on status
            if (adequacy.status) {
                if (existingDANCRecord) {
                    // Update existing record
                    try {
                        const updateData = {
                            subsectionName: sectionId,
                            informationStatus: adequacy.status,
                            observation: adequacy.status === 'Inadequate'
                                ? (adequacy.remarks || 'Document adequacy marked as Inadequate')
                                : 'Document adequacy check completed',
                            isactive: 1
                        };

                        const response = await updateDANC(orgId, appId, title, updateData);

                        if (response.success) {
                            setExistingDANCRecord(response.data);
                        } else {
                            throw new Error(response.message || 'Update failed');
                        }
                    } catch (updateError) {
                        console.error('DA-NC update failed:', updateError);
                    }
                } else {
                    // Create new record  
                    try {
                        const dancData = {
                            Orgid: parseInt(orgId),
                            Appid: parseInt(appId),
                            SectionName: title,
                            SubsectionName: sectionId,
                            InformationProvided: adequacy.remarks || '',
                            InformationStatus: adequacy.status,
                            Observation: adequacy.status === 'Inadequate'
                                ? (adequacy.remarks || 'Document adequacy marked as Inadequate')
                                : 'Document adequacy check completed',
                            Insertedby: currentUserId,
                            NCtype: 'Document Adequacy'
                        };

                        const response = await insertDANC(dancData);

                        if (response.data.success) {
                            setExistingDANCRecord(response.data.data);
                            setDancSuccess('Record created successfully');
                        } else {
                            throw new Error(response.message || 'Insert failed');
                        }
                    } catch (insertError) {
                        console.error('DA-NC creation failed:', insertError);
                        setDancError('Failed to create Record: ' + insertError.message);
                    }
                }
            }
        } catch (error) {
            console.error('Error submitting adequacy check:', error);
            setDancError('Error submitting adequacy check: ' + error.message);
        } finally {
            setDancLoading(false);
        }
    };

    return (
        <Box sx={{
            mb: 3,
            p: 3,
            backgroundColor: '#f8f9fa',
            borderRadius: 2,
            border: '2px solid #e9ecef',
            borderLeft: '4px solid #007bff'
        }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{
                    color: '#495057',
                    fontWeight: 600,
                    fontSize: '1.1rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                }}>
                    📋 Adequacy Check - {title}
                    {isAdequacyReadOnly && (
                        <Chip
                            label="🔒 Read Only"
                            size="small"
                            sx={{
                                bgcolor: '#f5f5f5',
                                color: '#757575',
                                fontWeight: 'bold',
                                fontSize: '0.7rem'
                            }}
                        />
                    )}
                </Typography>

                {(adequacy.status || existingDANCRecord) && (
                    <Chip
                        label={`DA-NC: ${adequacy.status || existingDANCRecord?.InformationStatus || 'Active'}`}
                        size="small"
                        sx={{
                            bgcolor: (adequacy.status || existingDANCRecord?.InformationStatus) === 'Adequate' ? '#28a745' : '#dc3545',
                            color: 'white',
                            fontWeight: 'bold'
                        }}
                    />
                )}
            </Box>

            {/* DA NC Error/Success Messages */}
            {dancError && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDancError('')}>
                    {dancError}
                </Alert>
            )}

            {dancSuccess && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setDancSuccess('')}>
                    {dancSuccess}
                </Alert>
            )}

            <Grid container spacing={3} alignItems="flex-start">
                <Grid item xs={12} md={4}>
                    <FormControl fullWidth sx={{
                        '& .MuiOutlinedInput-root': {
                            backgroundColor: 'white',
                            borderRadius: 2,
                            '&:hover': {
                                boxShadow: '0 2px 8px rgba(0,123,255,0.15)',
                            },
                            '&.Mui-focused': {
                                boxShadow: '0 4px 12px rgba(0,123,255,0.25)',
                            }
                        }
                    }}>
                        <InputLabel>Document Status</InputLabel>
                        <Select
                            value={adequacy.status}
                            onChange={isAdequacyReadOnly ? undefined : (e) => handleStatusChange(e.target.value)}
                            label="Document Status"
                            disabled={isAdequacyReadOnly}
                            sx={isAdequacyReadOnly ? {
                                backgroundColor: '#f5f5f5',
                                cursor: 'not-allowed',
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#e0e0e0',
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#e0e0e0',
                                }
                            } : undefined}
                        >
                            <MenuItem value="">-- Select Status --</MenuItem>
                            <MenuItem value="Adequate">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CheckCircleIcon sx={{ color: '#28a745', fontSize: '1rem' }} />
                                    Adequate
                                </Box>
                            </MenuItem>
                            <MenuItem value="Inadequate">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <ErrorIcon sx={{ color: '#dc3545', fontSize: '1rem' }} />
                                    Inadequate
                                </Box>
                            </MenuItem>
                        </Select>
                    </FormControl>
                </Grid>

                {adequacy.status === 'Inadequate' && (
                    <Grid item xs={12} md={8}>
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            label={isAdequacyReadOnly ? "Remarks (View Only)" : "Remarks (Required)"}
                            placeholder={isAdequacyReadOnly ? "View Only (Read-only)" : "Please provide specific comments about the inadequacies..."}
                            value={adequacy.remarks || ''}
                            onChange={isAdequacyReadOnly ? undefined : (e) => handleRemarksChange(e.target.value)}
                            required={!isAdequacyReadOnly}
                            variant="outlined"
                            InputProps={isAdequacyReadOnly ? {
                                readOnly: true,
                                style: {
                                    backgroundColor: '#f5f5f5',
                                    cursor: 'not-allowed'
                                }
                            } : undefined}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    backgroundColor: isAdequacyReadOnly ? '#f5f5f5' : 'white',
                                    borderRadius: 2,
                                    cursor: isAdequacyReadOnly ? 'not-allowed' : 'text',
                                    '&:hover': {
                                        boxShadow: isAdequacyReadOnly ? 'none' : '0 2px 8px rgba(220,53,69,0.15)',
                                    },
                                    '&.Mui-focused': {
                                        boxShadow: isAdequacyReadOnly ? 'none' : '0 4px 12px rgba(220,53,69,0.25)',
                                    }
                                }
                            }}
                        />
                        <Typography variant="caption" sx={{
                            color: '#6c757d',
                            display: 'block',
                            mt: 1,
                            fontStyle: 'italic'
                        }}>
                            {isAdequacyReadOnly
                                ? "Adequacy check is in read-only mode for stage 18a."
                                : "Please specify what needs to be corrected or improved."
                            }
                        </Typography>
                    </Grid>
                )}
            </Grid>

            {adequacy.status && (
                <Box sx={{ mt: 2 }}>
                    <Alert
                        severity={existingDANCRecord
                            ? (adequacy.status === 'Adequate' ? 'info' : 'warning')
                            : (adequacy.status === 'Adequate' ? 'success' : 'warning')
                        }
                        sx={{
                            borderRadius: 2,
                            '& .MuiAlert-icon': {
                                fontSize: '1.2rem'
                            }
                        }}
                    >
                        {isAdequacyReadOnly
                            ? '🔒 Adequacy check is in read-only mode for stage 18a. No modifications can be made.'
                            : (existingDANCRecord
                                ? (adequacy.status === 'Adequate'
                                    ? `✓ Record exists. Click Update to modify.`
                                    : `⚠ Record exists. Click Update to modify.`)
                                : (adequacy.status === 'Adequate'
                                    ? '✓ This section is marked as Adequate. Click Submit to create Record.'
                                    : '⚠ Please address the remarks above and submit to create Record.'))}
                    </Alert>

                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            variant="contained"
                            size="medium"
                            onClick={handleSubmit}
                            disabled={
                                isAdequacyReadOnly ||
                                !adequacy.status ||
                                (adequacy.status === 'Inadequate' && !adequacy.remarks?.trim()) ||
                                loading ||
                                dancLoading
                            }
                            sx={{
                                background: existingDANCRecord
                                    ? 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)'
                                    : (adequacy.status === 'Adequate'
                                        ? 'linear-gradient(135deg, #28a745 0%, #20c997 100%)'
                                        : 'linear-gradient(135deg, #ffc107 0%, #fd7e14 100%)'),
                                color: 'white',
                                fontWeight: 600,
                                px: 3,
                                py: 1,
                                borderRadius: 2,
                                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
                                },
                                '&:disabled': {
                                    background: '#e9ecef',
                                    color: '#6c757d',
                                    transform: 'none',
                                    boxShadow: 'none'
                                }
                            }}
                        >
                            {(loading || dancLoading) ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CircularProgress size={16} sx={{ color: 'white' }} />
                                    {existingDANCRecord ? 'Updating...' : 'Submitting...'}
                                </Box>
                            ) : (
                                <>📤 {existingDANCRecord ? 'Update Adequacy Check' : 'Submit Adequacy Check'}</>
                            )}
                        </Button>
                    </Box>
                </Box>
            )}
        </Box>
    );
});

const DeclarationSection = ({ data, onUpdate }) => {
    const navigate = useNavigate();

    // Router hooks for encrypted parameters
    const { orgId: encryptedOrgId, appId: encryptedAppId } = useParams();
    const location = useLocation();

    // Extract encrypted parameters from URL query params or route params
    const getOrgId = useCallback(() => {
        try {
            // First try to get from URL query parameters
            const urlParams = new URLSearchParams(location.search);
            const orgIdParam = urlParams.get('orgId');
            if (orgIdParam) {
                return decryptText(orgIdParam);
            }

            // Then try to get from route parameters
            if (encryptedOrgId) {
                return decryptText(encryptedOrgId);
            }

            return null;
        } catch (error) {
            console.error('DeclarationSection - Error decrypting orgId:', error);
            return null;
        }
    }, [encryptedOrgId, location.search]);

    const getAppId = useCallback(() => {
        try {
            // First try to get from URL query parameters
            const urlParams = new URLSearchParams(location.search);
            const appIdParam = urlParams.get('appId');
            if (appIdParam) {
                return decryptAppId(appIdParam);
            }

            // Then try to get from route parameters
            if (encryptedAppId) {
                return decryptAppId(encryptedAppId);
            }

            return null;
        } catch (error) {
            console.error('DeclarationSection - Error decrypting appId:', error);
            return null;
        }
    }, [encryptedAppId, location.search]);

    // State management
    const [declarationData, setDeclarationData] = useState({
        // Authorized representative details
        representativeName: '',
        representativeTitle: '',

        // Additional declaration
        termchk: false,
        finalDeclarationAgreed: false,

        // File upload
        shareholderDirectorFile: null
    });

    const [notification, setNotification] = useState({
        open: false,
        message: '',
        severity: 'info'
    });

    const [submitDialog, setSubmitDialog] = useState(false);
    const [passwordDialog, setPasswordDialog] = useState(false);
    const [password, setPassword] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [loading, setLoading] = useState(false);
    const [feesData, setFeesData] = useState(null);
    const [feesLoading, setFeesLoading] = useState(false);

    // Simplified state management
    const [fieldsDisabled, setFieldsDisabled] = useState(false);
    const [paymentStatus, setPaymentStatus] = useState(null); // null, 'failed', 'success'
    const [showSaveButton, setShowSaveButton] = useState(false);
    const [showPaymentButtons, setShowPaymentButtons] = useState(false);
    const [showSubmitButton, setShowSubmitButton] = useState(false);

    // Add loading flag to prevent multiple calls
    const [initialLoadComplete, setInitialLoadComplete] = useState(false);
    const [applicationStage, setApplicationStage] = useState(null);

    // Adequacy Check States
    const [adequacyChecks, setAdequacyChecks] = useState({});
    const [adequacyLoading, setAdequacyLoading] = useState(false);

    // Adequacy Check Handlers
    const handleAdequacyCheck = (sectionId, status) => {
        setAdequacyChecks(prev => ({
            ...prev,
            [sectionId]: {
                ...(prev[sectionId] || {}),
                status: status
            }
        }));
    };

    const handleAdequacyRemarks = (sectionId, remarks) => {
        setAdequacyChecks(prev => ({
            ...prev,
            [sectionId]: {
                ...(prev[sectionId] || {}),
                remarks: remarks
            }
        }));
    };

    const handleAdequacySubmit = async (sectionId) => {
        setAdequacyLoading(true);
        return new Promise((resolve) => {
            setTimeout(() => {
                setAdequacyChecks(prev => ({
                    ...prev,
                    [sectionId]: {
                        ...prev[sectionId],
                        isSubmitted: true,
                        submittedAt: new Date().toISOString()
                    }
                }));
                setAdequacyLoading(false);
                resolve();
            }, 500);
        });
    };

    // Get current application stage
    const getCurrentApplicationStage = async () => {
        try {
            const appId = getAppId();
            if (appId) {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/laboratory/application-stage/${appId}`);
                const result = await response.json();

                if (result.success && result.stage) {
                    setApplicationStage(result.stage);
                    console.log('Application stage loaded:', result.stage);
                } else {
                    setApplicationStage(null); // Stage not found, set to null
                    console.log('Stage not found, setting to null');
                }
            } else {
                setApplicationStage(null); // No appId, set to null
            }
        } catch (error) {
            console.error('Error determining application stage:', error);
            // Use null as default if API fails
            setApplicationStage(null);
        }
    };

    // Load declaration data and check payment status on page load
    useEffect(() => {
        loadDeclarationData();
        getCurrentApplicationStage(); // Load application stage on mount
        setInitialLoadComplete(true);
    }, [applicationStage]);

    // Load declaration data
    const loadDeclarationData = async () => {
        try {
            const orgId = getOrgId();
            const appId = getAppId();

            if (!orgId || !appId) return;

            // Get declaration data
            const response = await TestingLaboratoryService.getDeclarationData(orgId, appId);

            if (response.success && response.data) {
                console.log('Declaration data loaded:', response.data);

                // Handle the case where both name and title are in DeclNameOrg
                let repName = '';
                let repTitle = '';

                if (response.data.DeclNameOrg) {
                    // Split the combined field "Ayan, head of laboratory"
                    const parts = response.data.DeclNameOrg.split(',').map(part => part.trim());
                    repName = parts[0] || '';
                    repTitle = parts[1] || '';
                } else {
                    // Fallback to separate fields if they exist
                    repName = response.data.representativeName || '';
                    repTitle = response.data.representativeTitle || '';
                }

                // Process shareholderDirectorFile with S3 utils (matching Laboratory section)
                let processedFile = null;
                if (response.data.shareholderDirectorFile) {
                    // If it's a string (filename), process it to get signed URL
                    if (typeof response.data.shareholderDirectorFile === 'string') {
                        processedFile = await s3Utils.processForm153FileData(
                            response.data.shareholderDirectorFile,
                            'shareholderDirectorFile'
                        );
                    } else {
                        // If it's already an object, use it as-is
                        processedFile = response.data.shareholderDirectorFile;
                    }
                }

                // Update form fields
                setDeclarationData({
                    representativeName: repName,
                    representativeTitle: repTitle,
                    shareholderDirectorFile: processedFile,
                    termchk: response.data.termchk || false,
                    finalDeclarationAgreed: response.data.finalDeclarationAgreed || false
                });

                // Create modified data object for button visibility check
                const modifiedData = {
                    representativeName: repName,
                    representativeTitle: repTitle,
                    shareholderDirectorFile: processedFile,
                    termchk: response.data.termchk || false,
                    finalDeclarationAgreed: response.data.finalDeclarationAgreed || false
                };

                // Check if all fields are filled and determine button state
                checkButtonVisibility(modifiedData);

                // Check payment status with loaded data
                await checkPaymentStatus(modifiedData);
            } else {
                // No existing data - show save button disabled initially
                setShowSaveButton(true);
                // Check payment status with empty data
                await checkPaymentStatus();
            }

        } catch (error) {
            console.log('No existing declaration data found');
            setShowSaveButton(true);
            await checkPaymentStatus();
        }
    };

    // Check what buttons to show based on declaration data
    const checkButtonVisibility = (data = declarationData) => {
        const hasAllFields = data.representativeName &&
            data.representativeTitle &&
            data.shareholderDirectorFile &&
            data.finalDeclarationAgreed &&
            data.termchk;

        console.log('🔍 checkButtonVisibility - hasAllFields:', hasAllFields);
        console.log('🔍 checkButtonVisibility - data:', {
            representativeName: `"${data.representativeName}" (${typeof data.representativeName}) - ${!!data.representativeName}`,
            representativeTitle: `"${data.representativeTitle}" (${typeof data.representativeTitle}) - ${!!data.representativeTitle}`,
            shareholderDirectorFile: `${!!data.shareholderDirectorFile} (${typeof data.shareholderDirectorFile})`,
            finalDeclarationAgreed: `${data.finalDeclarationAgreed} (${typeof data.finalDeclarationAgreed})`,
            termchk: `${data.termchk} (${typeof data.termchk})`
        });

        if (hasAllFields) {
            // All fields filled - disable fields and hide save button
            console.log('✅ All fields complete - disabling fields and hiding save button');
            setFieldsDisabled(true);
            setShowSaveButton(false);
            // Payment buttons will be determined by payment status check
        } else {
            // Some fields missing - show save button, enable fields
            console.log('❌ Some fields missing - showing save button');
            setShowSaveButton(true);
            setFieldsDisabled(false);
            setShowPaymentButtons(false);
            setShowSubmitButton(false);
        }
    };

    // Check payment status from PaymentGateWayTracking table
    const checkPaymentStatus = async (dataToCheck = declarationData) => {
        try {
            const orgId = getOrgId();
            const appId = getAppId();

            if (!orgId || !appId) return;

            const response = await paymentService.checkPaymentStatus(orgId, appId);

            if (response.success && response.data) {
                if (response.data.order_status === 'failed' || response.data.order_status === 'Failed') {
                    setPaymentStatus('failed');

                    // Check if all fields are complete (use passed data to avoid timing issues)
                    const hasAllFields = dataToCheck.representativeName &&
                        dataToCheck.representativeTitle &&
                        dataToCheck.shareholderDirectorFile &&
                        dataToCheck.finalDeclarationAgreed &&
                        dataToCheck.termchk;


                    if (hasAllFields || fieldsDisabled) {
                        setShowPaymentButtons(true);
                    } else {
                        setShowPaymentButtons(false);
                    }
                    setShowSubmitButton(false);
                } else if (response.data.order_status === 'success' || response.data.order_status === 'Success') {
                    setPaymentStatus('success');
                    setShowPaymentButtons(false);


                    // Check if all fields are complete for submit button (use passed data to avoid timing issues)
                    const hasAllFields = dataToCheck.representativeName &&
                        dataToCheck.representativeTitle &&
                        dataToCheck.shareholderDirectorFile &&
                        dataToCheck.finalDeclarationAgreed &&
                        dataToCheck.termchk;

                    if ((hasAllFields || fieldsDisabled) && (applicationStage === "" || applicationStage === null)) {
                        setShowSubmitButton(true);
                    }
                    else {
                        setShowSubmitButton(false);
                    }
                }
            } else {
                // No payment record found - show payment buttons if fields are complete
                console.log('🔍 No payment record found');
                setPaymentStatus(null);

                // Check if all fields are complete (use passed data to avoid timing issues)
                const hasAllFields = dataToCheck.representativeName &&
                    dataToCheck.representativeTitle &&
                    dataToCheck.shareholderDirectorFile &&
                    dataToCheck.finalDeclarationAgreed &&
                    dataToCheck.termchk;

                if (hasAllFields || fieldsDisabled) {
                    console.log('✅ Showing payment buttons (no payment record)');
                    setShowPaymentButtons(true);
                } else {
                    console.log('❌ Not showing payment buttons - fields incomplete');
                }
                setShowSubmitButton(false);
            }
        } catch (error) {
            console.error('Error checking payment status:', error);
            setPaymentStatus(null);

            // Check if all fields are complete (use passed data to avoid timing issues)
            const hasAllFields = dataToCheck.representativeName &&
                dataToCheck.representativeTitle &&
                dataToCheck.shareholderDirectorFile &&
                dataToCheck.finalDeclarationAgreed &&
                dataToCheck.termchk;

            if (hasAllFields || fieldsDisabled) {
                setShowPaymentButtons(true);
            }
        }
    };





    // Auto-save when data changes (only if onUpdate is provided and data is meaningful)
    useEffect(() => {
        if (onUpdate && initialLoadComplete && Object.keys(declarationData).length > 0) {
            onUpdate(declarationData);
        }
    }, [declarationData, onUpdate, initialLoadComplete]);

    // Show notification helper
    const showNotification = (message, severity = 'info') => {
        setNotification({
            open: true,
            message,
            severity
        });
    };

    // Close notification
    const closeNotification = () => {
        setNotification(prev => ({ ...prev, open: false }));
    };

    // Handle checkbox changes
    const handleCheckboxChange = (fieldName) => (event) => {
        setDeclarationData(prev => ({
            ...prev,
            [fieldName]: event.target.checked
        }));
    };

    // Handle text field changes
    const handleTextFieldChange = (fieldName) => (event) => {
        setDeclarationData(prev => ({
            ...prev,
            [fieldName]: event.target.value
        }));
    };

    // Handle file upload - matching Laboratory section implementation
    const handleFileUpload = (fieldName, uploadData) => {
        if (uploadData === null || uploadData === undefined) {
            // File was deleted, remove from form data
            setDeclarationData(prev => ({
                ...prev,
                [fieldName]: null
            }));
            showNotification('Declaration file removed', 'info');
            return;
        }

        const fileData = {
            ...uploadData,
            fileName: uploadData.fileName || uploadData.originalName,
            filePath: uploadData.filePath || uploadData.url,
            uploadDate: new Date().toISOString(),
            fileSize: uploadData.fileSize,
            mimeType: uploadData.mimeType
        };

        setDeclarationData(prev => ({
            ...prev,
            [fieldName]: fileData
        }));

        if (uploadData && uploadData.fileName) {
            showNotification('Declaration file uploaded successfully', 'success');
        }

        console.log(`File uploaded for ${fieldName}:`, fileData);
    };

    // Check if all required fields are filled for saving declaration data
    const canSaveDeclaration = () => {
        return declarationData.representativeName?.trim() &&
            declarationData.representativeTitle?.trim() &&
            declarationData.finalDeclarationAgreed &&
            declarationData.termchk &&
            declarationData.shareholderDirectorFile;
    };

    // Watch for changes in form data to enable/disable save button
    useEffect(() => {
        if (showSaveButton && !fieldsDisabled) {
            // Form is editable - update save button state based on completion
            const isComplete = canSaveDeclaration();
            // Save button will be enabled/disabled based on canSaveDeclaration()
        }
    }, [declarationData, showSaveButton, fieldsDisabled]);

    // Handle declaration submission
    const handleSubmitDeclaration = async () => {
        try {
            setLoading(true);

            // Basic validation - check if all required fields are filled
            if (!canSaveDeclaration()) {
                showNotification('Please complete all required declarations before submitting', 'error');
                setLoading(false);
                return;
            }

            const appId = getAppId();
            const orgId = getOrgId();

            // Validate session data
            if (!appId || !orgId) {
                showNotification('Session data not found. Please login again.', 'error');
                setLoading(false);
                return;
            }

            // First submit the final declaration
            console.log('Submitting final declaration...');
            showNotification('Submitting application...', 'info');

            const result = await declarationService.submitFinalDeclaration(appId, orgId);

            if (result.success) {
                showNotification('Application Submitted Successfully', 'success');
                setSubmitDialog(false);

                // Log the successful submission
                console.log('Declaration submitted successfully:', {
                    appId: result.data.appId,
                    orgId: result.data.orgId,
                    stage: result.data.stage,
                    submittedAt: result.data.submittedAt,
                    clientIP: result.data.clientIP
                });

                // Update parent component if needed
                if (onUpdate) {
                    onUpdate({
                        ...declarationData,
                        isSubmitted: true,
                        submissionResult: result.data
                    });
                }

                // Redirect to preview report page with auto-download parameter
                console.log('Redirecting to preview report for PDF generation...');
                showNotification('Redirecting to generate preview report...', 'info');

                // Add a small delay to ensure the user sees the success message
                setTimeout(() => {
                    navigate(`/main/testing-preview-report?appId=${encryptAppId(appId)}&orgId=${encryptText(orgId)}&observation=${encryptText('False')}&autoDownload=true&fromSubmission=true`);
                }, 1500);

                return;
            } else {
                throw new Error(result.message || 'Failed to submit declaration');
            }

        } catch (error) {
            console.error('Error submitting declaration:', error);
            showNotification(`Error submitting declaration: ${error.message}`, 'error');
        } finally {
            setLoading(false);
        }
    };

    // Handle save declaration data
    const handleSaveDeclaration = async () => {
        try {
            setLoading(true);

            // Validate required fields before saving
            if (!canSaveDeclaration()) {
                const missingFields = [];
                if (!declarationData.representativeName?.trim()) missingFields.push('Representative Name');
                if (!declarationData.representativeTitle?.trim()) missingFields.push('Representative Title');
                if (!declarationData.finalDeclarationAgreed) missingFields.push('Final Declaration Agreement');
                if (!declarationData.termchk) missingFields.push('Terms & Conditions Agreement');
                if (!declarationData.shareholderDirectorFile) missingFields.push('Shareholders and Directors Declaration File');

                showNotification(`Please complete the following required fields: ${missingFields.join(', ')}`, 'error');
                return;
            }

            const orgId = getOrgId();
            const appId = getAppId();

            // Combine name and title into DeclNameOrg field
            const combinedNameTitle = `${declarationData.representativeName.trim()}, ${declarationData.representativeTitle.trim()}`;

            const saveData = {
                DeclNameOrg: combinedNameTitle,
                representativeName: declarationData.representativeName.trim(),
                representativeTitle: declarationData.representativeTitle.trim(),
                shareholderDirectorFile: declarationData.shareholderDirectorFile,
                termchk: declarationData.termchk,
                finalDeclarationAgreed: declarationData.finalDeclarationAgreed
            };

            const response = await TestingLaboratoryService.saveDeclarationData(orgId, appId, saveData);

            if (response.success) {
                showNotification('Declaration data saved successfully', 'success');

                // After successful save, reload declaration data to update UI
                await loadDeclarationData();
            } else {
                showNotification(`Error saving declaration data: ${response.message}`, 'error');
            }

        } catch (error) {
            console.error('Error saving declaration data:', error);
            showNotification(`Error saving declaration data: ${error.message}`, 'error');
        } finally {
            setLoading(false);
        }
    };

    // Handle preview report button click
    const handlePreviewReport = () => {
        const orgId = getOrgId();
        const appId = getAppId();

        if (!orgId || !appId) {
            showNotification('Organization ID or Application ID not found', 'error');
            return;
        }

        // Encrypt parameters before constructing URL
        const encryptedAppId = encryptAppId(appId);
        const encryptedOrgId = encryptText(orgId);
        const encryptedObservation = encryptText('false');

        const previewUrl = `/main/testing-preview-report?appId=${encryptedAppId}&orgId=${encryptedOrgId}&observation=${encryptedObservation}`;

        // Open in new tab/window
        window.open(previewUrl, '_blank');

        showNotification('Opening preview report in new tab', 'info');
    };

    // Handle proceed to payment button click
    const handleProceedToPay = () => {
        setPasswordDialog(true);
        setPassword('');
        setPasswordError('');
    };

    // Handle password verification using login password
    const handlePasswordVerification = async () => {
        try {
            setLoading(true);
            const orgId = getOrgId();

            if (!orgId) {
                setPasswordError('Organization ID not found. Please login again.');
                setLoading(false);
                return;
            }

            // Verify password using authService (matches your ASP.NET verifyPass method)
            const verificationResult = await paymentService.verifyPaymentPassword(orgId, password);

            console.log('Password verification result:', verificationResult);

            if (verificationResult && verificationResult.success && verificationResult.isValid) {
                setPasswordDialog(false);
                setPasswordError(''); // Clear any previous errors

                // Navigate to payment page directly
                navigate('/main/testing/payment');

                showNotification('Password verified. Redirecting to payment gateway...', 'success');
            } else {
                // Handle password verification failure
                const errorMessage = verificationResult?.message || 'Password is incorrect';
                setPasswordError(errorMessage);

                // Don't redirect to login page - let user try again
                console.log('Password verification failed:', verificationResult);
            }
        } catch (error) {
            console.error('Password verification error:', error);

            // Handle different types of errors
            if (error.response && error.response.status === 500) {
                setPasswordError('Server error. Please try again later.');
            } else if (error.response && error.response.status === 404) {
                setPasswordError('User not found. Please check your organization ID.');
            } else if (error.message && error.message.includes('Network')) {
                setPasswordError('Network error. Please check your connection.');
            } else {
                setPasswordError('Password verification failed. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    // Handle password dialog close
    const handlePasswordDialogClose = () => {
        setPasswordDialog(false);
        setPassword('');
        setPasswordError('');
    };

    return (
        <Box sx={{ p: 3 }}>
            <Paper elevation={3} sx={{ p: 3 }}>
                {/* Header */}
                <Box sx={{ mb: 4, textAlign: 'center' }}>
                    <Typography variant="h4" component="h1" sx={{
                        fontWeight: 'bold',
                        color: 'primary.main',
                        mb: 2
                    }}>
                        Declaration for NABL Accreditation
                    </Typography>
                    <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 2 }}>
                        Terms & Conditions for Obtaining and Maintaining NABL Accreditation
                    </Typography>

                    {/* Completion Status Indicator */}
                    <Alert severity="success" sx={{ maxWidth: '600px', mx: 'auto' }}>
                        <Typography variant="body2">
                            ✅ All enclosure requirements have been completed. You can now proceed with the declaration.
                        </Typography>
                    </Alert>
                </Box>

                {/* Adequacy Check Section */}
                <Box sx={{ mb: 4 }}>
                    <AdequacyCheckSection
                        sectionId="declaration"
                        title="Declaration Section"
                        applicationStage={applicationStage}
                        orgId={getOrgId()}
                        appId={getAppId()}
                        adequacyChecks={adequacyChecks}
                        handleAdequacyCheck={handleAdequacyCheck}
                        handleAdequacyRemarks={handleAdequacyRemarks}
                        handleAdequacySubmit={handleAdequacySubmit}
                        loading={adequacyLoading}
                        currentUser={localStorage.getItem('userName') || 'System User'}
                        currentUserId={parseInt(localStorage.getItem('userId') || '0')}
                    />
                </Box>

                {/* Terms & Conditions Content */}
                <Card elevation={2} sx={{ mb: 4 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                            <GavelIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                            Terms & Conditions
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ textAlign: 'justify', lineHeight: 1.6 }}>
                            The terms and conditions mentioned in this document is considered as accreditation agreement between NABL and Conformity Assessment Body (CAB). By accepting these terms and conditions, it is implied that a CAB as an applicant and after obtaining accreditation agrees to comply at all times with all Terms and Conditions for NABL Accreditation. Any violation of this terms and conditions (Accreditation agreement) shall result in adverse decision as per NABL 216 'Procedures for Dealing with Adverse Decisions' including legal action.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ textAlign: 'justify', lineHeight: 1.6 }}>
                            The Conformity Assessment Body {'{Testing Laboratory/ Calibration Laboratory/ Medical Testing Laboratory/ Proficiency Testing Provider (PTP)/ Reference Material Producer (RMP)}'} that are applicant or accredited by NABL shall be required to fulfill the following terms and conditions:
                        </Typography>
                        <List sx={{ pl: 2, listStyleType: "decimal", "& .MuiListItem-root": { display: "list-item" } }}>
                            {/* Point 1 */}
                            <ListItem sx={{ pl: 0 }}>
                                <ListItemText
                                    primary={
                                        <Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>
                                            The Conformity Assessment Body (CAB) shall carry out its Testing / Calibration /
                                            Medical / PTP / RMP / Biobank activities in such a way as to meet the requirements
                                            of relevant standard (eg. ISO/IEC 17025 or ISO 15189 or ISO/IEC 17043 or ISO 17034
                                            or ISO 20387, whichever is applicable), appropriate NABL specific
                                            criteria/application documents (wherever applicable) and other policies of NABL,
                                            as relevant to the accredited scope at all times.
                                        </Typography>
                                    }
                                />
                            </ListItem>

                            {/* Point 2 */}
                            <ListItem sx={{ pl: 0 }}>
                                <ListItemText
                                    primary={
                                        <Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>
                                            CAB shall ensure that the latest versions of NABL documents are available with it
                                            and are effectively implemented.
                                        </Typography>
                                    }
                                />
                            </ListItem>

                            {/* Point 3 */}
                            <ListItem sx={{ pl: 0 }}>
                                <ListItemText
                                    primary={
                                        <Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>
                                            CAB shall familiarize itself with all relevant standards, NABL documents etc.
                                            applicable for particular accreditation scheme.
                                        </Typography>
                                    }
                                />
                            </ListItem>

                            {/* Point 4 */}
                            <ListItem sx={{ pl: 0 }}>
                                <ListItemText
                                    primary={
                                        <>
                                            <Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>
                                                The CAB shall have a valid legal entity status as given below:
                                            </Typography>

                                            {/* Sub List */}
                                            <List
                                                sx={{
                                                    pl: 4,
                                                    mt: 1,
                                                    listStyleType: "lower-alpha",
                                                    "& .MuiListItem-root": { display: "list-item" },
                                                }}
                                            >
                                                <ListItem sx={{ pl: 0 }}>
                                                    <ListItemText
                                                        primary="One Person Company (Registration certificate under The Companies Act, 2013)"
                                                    />
                                                </ListItem>

                                                <ListItem sx={{ pl: 0 }}>
                                                    <ListItemText
                                                        primary="Limited Liability Partnership (Registration certificate under The Limited Liability Partnership Act, 2008)"
                                                    />
                                                </ListItem>

                                                <ListItem sx={{ pl: 0 }}>
                                                    <ListItemText
                                                        primary="Company (Registration certificate under The Companies Act, 1956 or 2013)"
                                                    />
                                                </ListItem>

                                                <ListItem sx={{ pl: 0 }}>
                                                    <ListItemText
                                                        primary="Societies / Trust (Registration certificate under Societies Registration Act, 1860 / Registration under The Indian Trusts Act, 1882)"
                                                    />
                                                </ListItem>
                                            </List>
                                        </>
                                    }
                                />
                            </ListItem>
                            {/* Point 5 */}
                            <ListItem sx={{ pl: 0 }}>
                                <ListItemText
                                    primary={
                                        <Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>
                                            CAB (Conformity Assessment Body) shall provide all necessary information about its
                                            antecedents as required by NABL which shall include but not limited to its background
                                            information i.e. history, establishment, ownership, and organizational structure,
                                            Key Personnel, Previous Experience, Legal Compliance, Disputes and Complaints if any,
                                            Quality Management System, Financial Information if required, Conflict of Interest,
                                            Accreditations and Certifications from any other body or Any other information,
                                            deemed necessary by NABL to analyse and evaluate the CAB's eligibility for
                                            accreditation. By submitting the application for accreditation, the CAB expressly
                                            consents to the collection, use, and disclosure to NABL of the provided information
                                            for the purpose of evaluating the accreditation application and ensuring compliance
                                            with accreditation standards. NABL reserves the right to verify the accuracy and
                                            authenticity of the provided information. Failure to provide accurate and complete
                                            information or any attempt to withhold relevant details shall result in the rejection
                                            of the accreditation application.
                                        </Typography>
                                    }
                                />
                            </ListItem>

                            {/* Point 6 */}
                            <ListItem sx={{ pl: 0 }}>
                                <ListItemText
                                    primary={
                                        <Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>
                                            Accreditation granted by NABL signifies conformity of the laboratory’s management
                                            system and technical competence with the applicable accreditation criteria at the
                                            time of assessment and does not constitute an endorsement, certification, warranty,
                                            or guarantee by NABL of the laboratory’s test or calibration results, services, or
                                            continued performance. NABL shall not be responsible or liable for the accuracy,
                                            validity, or use of any test or calibration results, reports, opinions, or services
                                            provided by the accredited laboratory, nor for any contractual, commercial, or
                                            statutory obligations between the laboratory and its clients or third parties.
                                            Nothing contained herein shall be construed as limiting NABL’s functions, powers,
                                            or rights under the accreditation agreement, nor as excluding liability arising from
                                            fraud or willful misconduct or action specifically attributable to NABL under the
                                            applicable law.
                                        </Typography>
                                    }
                                />
                            </ListItem>

                            {/* Point 7 */}
                            <ListItem sx={{ pl: 0 }}>
                                <ListItemText
                                    primary={
                                        <Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>
                                            Accreditation granted by NABL is voluntary in nature and does not replace, waive,
                                            or substitute compliance with any applicable law, rule, regulation, or statutory
                                            requirement, except where such accreditation is expressly mandated by a Government
                                            authority or regulatory body. In the event of any inconsistency, conflict, or
                                            contradiction between the provisions of this accreditation agreement, or any NABL
                                            guideline and the applicable laws, rules, regulations, or statutory directions in
                                            force, the provisions of such applicable law shall prevail to the extent of such
                                            inconsistency.
                                        </Typography>
                                    }
                                />
                            </ListItem>

                            {/* Point 8 */}
                            <ListItem sx={{ pl: 0 }}>
                                <ListItemText
                                    primary={
                                        <Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>
                                            Accreditation granted by NABL is limited solely to the assessment, recognition and
                                            attestation of the technical competence of the Conformity Assessment Body (CAB) in
                                            accordance with applicable accreditation criteria. Such accreditation shall not be
                                            construed as a permission, authorization, license, or approval to conduct any
                                            business, commercial, clinical, diagnostic, or operational activity.
                                        </Typography>
                                    }
                                />
                            </ListItem>

                            {/* Point 9 */}
                            <ListItem sx={{ pl: 0 }}>
                                <ListItemText
                                    primary={
                                        <Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>
                                            It is the responsibility of CAB to obtain and maintain statutory and regulatory
                                            approvals, licenses, registrations and permissions as required under the applicable
                                            laws and abide by the National / Regional / State / Local regulatory requirements /
                                            Acts / Rules / Legal orders / Court Decisions / Orders issued by Government /
                                            Statutory Bodies as applicable and effective from time to time.
                                        </Typography>
                                    }
                                />
                            </ListItem>

                            {/* Point 10 */}
                            <ListItem sx={{ pl: 0 }}>
                                <ListItemText
                                    primary={
                                        <Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>
                                            The CAB shall identify and define various activities which they are involved in,
                                            and ensure that it does not lead to any potential conflict of interest.
                                        </Typography>
                                    }
                                />
                            </ListItem>

                            {/* Point 11 */}
                            <ListItem sx={{ pl: 0 }}>
                                <ListItemText
                                    primary={
                                        <Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>
                                            The CAB shall have available qualified and trained personnel for applied and/or
                                            accredited scope including sampling (wherever applicable).
                                        </Typography>
                                    }
                                />
                            </ListItem>

                            {/* Point 12 */}
                            <ListItem sx={{ pl: 0 }}>
                                <ListItemText
                                    primary={
                                        <Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>
                                            Wherever applicable, the CAB shall follow the test methods as mentioned in the
                                            current National or International standards and/or as stipulated by regulatory
                                            bodies. Where such methods do not exist, other validated methods may be followed.
                                            In case CAB uses in-house validated methods, the validation data should be
                                            submitted along with the application.
                                        </Typography>
                                    }
                                />
                            </ListItem>

                            {/* Point 13 */}
                            <ListItem sx={{ pl: 0 }}>
                                <ListItemText
                                    primary={
                                        <Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>
                                            CAB shall ensure the capability with respect to applied scope of accreditation
                                            (availability of resources not limited to personnel, equipment, CRM, method
                                            validation etc.) before applying to NABL and thereafter at all times during the
                                            accredited period.
                                        </Typography>
                                    }
                                />
                            </ListItem>

                            {/* Point 14 */}
                            <ListItem sx={{ pl: 0 }}>
                                <ListItemText
                                    primary={
                                        <Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>
                                            The CAB shall promptly inform NABL of any changes in its organization, personnel,
                                            facilities, scope of services, or any other significant factors that may impact
                                            its accreditation status.
                                        </Typography>
                                    }
                                />
                            </ListItem>

                            {/* Point 15 */}
                            <ListItem sx={{ pl: 0 }}>
                                <ListItemText
                                    primary={
                                        <Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>
                                            Applicant CAB shall successfully participate in at least one Proficiency Testing
                                            (PT) program prior to gaining accreditation in each discipline applied. There may
                                            be situation where PT is not available. Requirements are specified in NABL 163 and
                                            the same shall be followed for PT participation or otherwise.
                                        </Typography>
                                    }
                                />
                            </ListItem>
                            {/* Point 16 */}
                            <ListItem sx={{ pl: 0 }}>
                                <ListItemText
                                    primary={
                                        <Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>
                                            CAB shall submit the completed application for renewal of accreditation well in
                                            advance (give sufficient time to process) prior to the expiry of accreditation and
                                            also agree to undergo assessment as per the schedule proposed by NABL to maintain
                                            continuity in accreditation cycle. Application submitted after expiry of
                                            accreditation will be considered as fresh application. The assessment (onsite)
                                            interval shall not exceed 24 months and in case if exceeds, then it will be treated
                                            afresh.
                                        </Typography>
                                    }
                                />
                            </ListItem>

                            {/* Point 17 */}
                            <ListItem sx={{ pl: 0 }}>
                                <ListItemText
                                    primary={
                                        <>
                                            <Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>
                                                The CAB shall offer full cooperation to NABL or its representative or member of
                                                team evaluating/assessing during the conduct of assessments (including onsite
                                                surveillance), any additional assessments, such as additional surveillance
                                                assessments based on risk, unannounced assessment/visit (including visit for
                                                investigation of complaints related to NABL accreditation against the CAB). The
                                                cooperation by the CAB shall include access to, but not limited to, the following
                                                activities:
                                            </Typography>

                                            <List
                                                sx={{
                                                    pl: 4,
                                                    mt: 1,
                                                    listStyleType: "lower-alpha",
                                                    "& .MuiListItem-root": { display: "list-item" },
                                                }}
                                            >
                                                <ListItem sx={{ pl: 0 }}>
                                                    <ListItemText primary="Undergoing assessments in stipulated time intervals / whenever NABL considered it as required." />
                                                </ListItem>

                                                <ListItem sx={{ pl: 0 }}>
                                                    <ListItemText primary="Providing access to all areas of operations related to CAB including permanent site, site, mobile / temporary, subcontractor / external provider premises, wherever relevant and applicable." />
                                                </ListItem>

                                                <ListItem sx={{ pl: 0 }}>
                                                    <ListItemText primary="Undertaking any check / inspection to verify the capability of the CAB for the applied and/or accredited scope." />
                                                </ListItem>

                                                <ListItem sx={{ pl: 0 }}>
                                                    <ListItemText primary="Witnessing the activities being performed relevant to accreditation." />
                                                </ListItem>

                                                <ListItem sx={{ pl: 0 }}>
                                                    <ListItemText primary="Assessing the competence of the staff (including staff working in shift operations / at permanent site, at site, at mobile facility) during assessment." />
                                                </ListItem>

                                                <ListItem sx={{ pl: 0 }}>
                                                    <ListItemText primary="Access to all relevant information and documentation and records." />
                                                </ListItem>

                                                <ListItem sx={{ pl: 0 }}>
                                                    <ListItemText primary="Access to those documents that provide insight into the level of independence and impartiality of the CAB from its own activities and its related bodies, if applicable." />
                                                </ListItem>

                                                <ListItem sx={{ pl: 0 }}>
                                                    <ListItemText primary="Access to all records pertaining to competence of the CAB personnel." />
                                                </ListItem>

                                                <ListItem sx={{ pl: 0 }}>
                                                    <ListItemText primary="Assessing the competence of the persons who are responsible to report, review and authorization of the results." />
                                                </ListItem>

                                                <ListItem sx={{ pl: 0 }}>
                                                    <ListItemText primary="Assessing the competence of the persons who are responsible for opinion and interpretation." />
                                                </ListItem>
                                            </List>
                                        </>
                                    }
                                />
                            </ListItem>

                            {/* Point 18 */}
                            <ListItem sx={{ pl: 0 }}>
                                <ListItemText
                                    primary={
                                        <Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>
                                            The CAB shall maintain the highest level of impartiality, integrity, and
                                            professionalism throughout the accreditation process as well as accreditation
                                            period. It shall avoid any actions or relationships that could compromise its
                                            independence or objectivity.
                                        </Typography>
                                    }
                                />
                            </ListItem>

                            {/* Point 19 */}
                            <ListItem sx={{ pl: 0 }}>
                                <ListItemText
                                    primary={
                                        <Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>
                                            The CAB shall establish and maintain an effective management system, adhering to
                                            recognized international or any other standards by NABL, to ensure consistent and
                                            reliable conformity assessment services.
                                        </Typography>
                                    }
                                />
                            </ListItem>

                            {/* Point 20 */}
                            <ListItem sx={{ pl: 0 }}>
                                <ListItemText
                                    primary={
                                        <Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>
                                            The CAB shall cooperate fully with NABL during assessments, audits,
                                            investigations, and any other activities related to the accreditation process. It
                                            shall provide all necessary information and access required by NABL to evaluate its
                                            compliance with accreditation standards.
                                        </Typography>
                                    }
                                />
                            </ListItem>

                            {/* Point 21 */}
                            <ListItem sx={{ pl: 0 }}>
                                <ListItemText
                                    primary={
                                        <Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>
                                            The CAB shall have, where applicable, legally enforceable arrangements with their
                                            customer/client that commit the customers/clients to provide, on request, access to
                                            NABL assessment team to assess the CAB’s performance when carrying out conformity
                                            assessment activities at the customer/client’s site.
                                        </Typography>
                                    }
                                />
                            </ListItem>

                            {/* Point 22 */}
                            <ListItem sx={{ pl: 0 }}>
                                <ListItemText
                                    primary={
                                        <>
                                            <Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>
                                                An accredited CAB shall also demonstrate the following during the validity period
                                                of its accreditation:
                                            </Typography>

                                            <List
                                                sx={{
                                                    pl: 4,
                                                    mt: 1,
                                                    listStyleType: "lower-alpha",
                                                    "& .MuiListItem-root": { display: "list-item" },
                                                }}
                                            >
                                                <ListItem sx={{ pl: 0 }}>
                                                    <ListItemText primary="compliance with the terms of this agreement" />
                                                </ListItem>

                                                <ListItem sx={{ pl: 0 }}>
                                                    <ListItemText primary="continuing conformity with the relevant standard (eg. ISO/IEC 17025 or ISO 15189 or ISO/IEC 17043 or ISO 17034 or ISO 20387, whichever is applicable) and appropriate NABL specific criteria/application documents (wherever applicable)" />
                                                </ListItem>

                                                <ListItem sx={{ pl: 0 }}>
                                                    <ListItemText primary="continuing competence within the scope of its accreditation" />
                                                </ListItem>

                                                <ListItem sx={{ pl: 0 }}>
                                                    <ListItemText primary="willingness to give such undertakings as NABL may reasonably require" />
                                                </ListItem>
                                            </List>
                                        </>
                                    }
                                />
                            </ListItem>

                            {/* Point 23 */}
                            <ListItem sx={{ pl: 0 }}>
                                <ListItemText
                                    primary={
                                        <Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>
                                            During the accreditation period, the CAB shall claim accreditation for only those
                                            premises, and scope for which it has been accredited (as stated in Accreditation
                                            Certificate and scope of Accreditation).
                                        </Typography>
                                    }
                                />
                            </ListItem>
                            {/* Point 24 */}
                            <ListItem sx={{ pl: 0 }}>
                                <ListItemText
                                    primary={
                                        <Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>
                                            Applicant CABs shall not claim to be NABL Accredited or use NABL symbol/logo before
                                            receiving the communication regarding grant of accreditation from NABL. In case it
                                            is found that CAB is claiming to be NABL Accredited or using NABL symbol/logo in
                                            applicant stage or misleading customers about its accreditation status in any way,
                                            this may lead to rejection of application or termination of the assessment process
                                            or denial of accreditation. Legal action may also be initiated.
                                        </Typography>
                                    }
                                />
                            </ListItem>

                            {/* Point 25 */}
                            <ListItem sx={{ pl: 0 }}>
                                <ListItemText
                                    primary={
                                        <Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>
                                            Accredited CAB shall adhere to NABL 133 for the use of NABL symbol, use of NABL
                                            Accredited CAB Combined ILAC MRA Mark as well as any claim of accreditation of the
                                            CAB. CAB shall ensure that it fully conforms to the requirements of NABL and
                                            ISO/IEC 17025 or ISO 15189 or ISO/IEC 17043 or ISO 17034 or ISO 20387 (as
                                            applicable) for claiming accreditation status while making references of its NABL
                                            accreditation in communication media. During the period of suspension or after
                                            withdrawal of accreditation, suitable legal action may be taken by NABL in case CAB
                                            falsely claims to be NABL accredited.
                                        </Typography>
                                    }
                                />
                            </ListItem>

                            {/* Point 26 */}
                            <ListItem sx={{ pl: 0 }}>
                                <ListItemText
                                    primary={
                                        <Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>
                                            Accredited CAB shall ensure that the copy of the test report/certificate, medical
                                            test report/certificate, calibration certificate/report, PT report and RM document
                                            stored/retained shall be an exact replica of the report issued to the customer
                                            including the format of the report header & footer, and NABL symbol.
                                        </Typography>
                                    }
                                />
                            </ListItem>

                            {/* Point 27 */}
                            <ListItem sx={{ pl: 0 }}>
                                <ListItemText
                                    primary={
                                        <Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>
                                            CAB shall use NABL accredited CAB Combined ILAC MRA Mark only after obtaining
                                            written permission from NABL and the usage shall be restricted to the period of
                                            validity of the permission as informed by NABL.
                                        </Typography>
                                    }
                                />
                            </ListItem>

                            {/* Point 28 */}
                            <ListItem sx={{ pl: 0 }}>
                                <ListItemText
                                    primary={
                                        <Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>
                                            Accredited CAB shall make it clear to its customer that its activities falling
                                            under accredited scope in no way imply that the product so tested or equipment
                                            calibrated or PT scheme offered or Reference Material (RM) produced is approved by
                                            NABL.
                                        </Typography>
                                    }
                                />
                            </ListItem>

                            {/* Point 29 */}
                            <ListItem sx={{ pl: 0 }}>
                                <ListItemText
                                    primary={
                                        <Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>
                                            The accredited CAB is neither allowed to make misleading / unauthorized statement
                                            regarding its accreditation nor refer to accreditation in such a way so as to imply
                                            that a product, process, service, management system or person is approved by NABL.
                                        </Typography>
                                    }
                                />
                            </ListItem>

                            {/* Point 30 */}
                            <ListItem sx={{ pl: 0 }}>
                                <ListItemText
                                    primary={
                                        <Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>
                                            The CAB shall pay application fees for accreditation, renewal of accreditation,
                                            expenses towards travel, boarding & lodging for any kind of assessment including
                                            supplementary visit, on-site surveillance, desktop surveillance, re-assessment and
                                            annual accreditation fees as determined from time to time, by NABL.
                                        </Typography>
                                    }
                                />
                            </ListItem>

                            {/* Point 31 */}
                            <ListItem sx={{ pl: 0 }}>
                                <ListItemText
                                    primary={
                                        <>
                                            <Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>
                                                The CAB shall inform NABL without delay (within 15 days) of significant changes
                                                relevant to accreditation, such as:
                                            </Typography>

                                            <List
                                                sx={{
                                                    pl: 4,
                                                    mt: 1,
                                                    listStyleType: "lower-alpha",
                                                    "& .MuiListItem-root": { display: "list-item" },
                                                }}
                                            >
                                                <ListItem sx={{ pl: 0 }}><ListItemText primary="its legal, commercial, ownership or organizational status." /></ListItem>
                                                <ListItem sx={{ pl: 0 }}><ListItemText primary="its resources not limited to personnel, facilities, equipment to manage and perform its activities, authorized personnel responsible to report, review and authorization of the results." /></ListItem>
                                                <ListItem sx={{ pl: 0 }}><ListItemText primary="premises." /></ListItem>
                                                <ListItem sx={{ pl: 0 }}><ListItemText primary="scope of accreditation." /></ListItem>
                                                <ListItem sx={{ pl: 0 }}><ListItemText primary="External Service Provider/Subcontractor, Production and/or characterization techniques (PTP/RMP)." /></ListItem>
                                                <ListItem sx={{ pl: 0 }}><ListItemText primary="Major changes in the operation of the CAB, for example, closure of the CAB for long duration, say more than 6 months." /></ListItem>
                                                <ListItem sx={{ pl: 0 }}><ListItemText primary="any other factor that may affect the ability of the CAB to fulfill the requirements of NABL accreditation including applicable policies & standards." /></ListItem>
                                                <ListItem sx={{ pl: 0 }}><ListItemText primary="any other change in information submitted in application." /></ListItem>
                                            </List>
                                        </>
                                    }
                                />
                            </ListItem>

                            {/* Point 32 */}
                            <ListItem sx={{ pl: 0 }}>
                                <ListItemText primary={<Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>The CAB shall regularly visit NABL website to keep itself updated with the latest versions of NABL documents as well as the NABL newsletter or any other relevant information concerning accreditation provided on the NABL website.</Typography>} />
                            </ListItem>

                            {/* Point 33 */}
                            <ListItem sx={{ pl: 0 }}>
                                <ListItemText primary={<Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>CAB shall always have a dedicated person responsible for implementation of Management System.</Typography>} />
                            </ListItem>

                            {/* Point 34 */}
                            <ListItem sx={{ pl: 0 }}>
                                <ListItemText primary={<Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>The CAB shall unambiguously and truthfully declare the name of any individual and organization that has provided consultancy for preparing towards NABL accreditation, at the relevant section in the application form.</Typography>} />
                            </ListItem>

                            {/* Point 35 */}
                            <ListItem sx={{ pl: 0 }}>
                                <ListItemText primary={<Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>Where a CAB subcontracts a substantial or critical part of the accredited activities that work shall be placed with another CAB that is accredited by NABL.</Typography>} />
                            </ListItem>

                            {/* Point 36 */}
                            <ListItem sx={{ pl: 0 }}>
                                <ListItemText primary={<Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>The accredited CAB shall commit to comply with the changes initiated by NABL in its accreditation criteria, policies and procedures. The CAB shall inform NABL when such alterations under the agreed time frame have been completed.</Typography>} />
                            </ListItem>

                            {/* Point 37 */}
                            <ListItem sx={{ pl: 0 }}>
                                <ListItemText primary={<Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>The CAB shall take all necessary actions and discharge all non-conformities raised during the assessment within 30 days to avoid any adverse decisions against it as detailed in NABL 216. The same shall be verified to the satisfaction of NABL. The final decision on accreditation shall rest with NABL.</Typography>} />
                            </ListItem>

                            {/* Point 38 */}
                            <ListItem sx={{ pl: 0 }}>
                                <ListItemText primary={<Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>CAB shall not use its NABL accreditation in such a manner as to bring NABL into disrepute.</Typography>} />
                            </ListItem>

                            {/* Point 39 */}
                            <ListItem sx={{ pl: 0 }}>
                                <ListItemText primary={<Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>At any point in the application or initial process, if there is evidence of fraudulent behavior and/or if the CAB has intentionally provided false information or conceals information, NABL shall reject the application or terminate the assessment process or denial of accreditation.</Typography>} />
                            </ListItem>

                            {/* Point 40 */}
                            <ListItem sx={{ pl: 0 }}>
                                <ListItemText primary={<Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>If evidence is found of fraudulent behaviour or the CAB intentionally provides false information or conceals information, NABL shall initiate adverse action as per NABL 216.</Typography>} />
                            </ListItem>

                            {/* Point 41 */}
                            <ListItem sx={{ pl: 0 }}>
                                <ListItemText primary={<Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>The accredited CAB upon suspension, withdrawal of accreditation or debar from reapplying (however determined) shall forthwith discontinue claim of accreditation in any form in all advertising material and return the accreditation certificates to NABL, if applicable.</Typography>} />
                            </ListItem>

                            {/* Point 42 */}
                            <ListItem sx={{ pl: 0 }}>
                                <ListItemText primary={<Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>CAB shall inform its affected clients of the suspension, scope reduction, withdrawal of accreditation (and debar from reapplying, if applicable) and the associated consequences, without undue delay.</Typography>} />
                            </ListItem>

                            {/* Point 43 */}
                            <ListItem sx={{ pl: 0 }}>
                                <ListItemText primary={<Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>Accredited CAB can voluntarily withdraw accreditation by giving notice in writing to NABL (and by surrendering the accreditation certificates, applicable) after clearing the outstanding amount, if any.</Typography>} />
                            </ListItem>

                            {/* Point 44 */}
                            <ListItem sx={{ pl: 0 }}>
                                <ListItemText primary={<Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>The CAB shall inform NABL, if any of the proposed assessor(s) happens to be their consultant or associated with the CAB in any other capacity (personal or professional).</Typography>} />
                            </ListItem>

                            {/* Point 45 */}
                            <ListItem sx={{ pl: 0 }}>
                                <ListItemText primary={<Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>NABL shall not be held responsible for any legal or financial liability arising out of activities of any of its accredited CAB involving in any accidental or consequential damages to personnel / equipment / products at any time.</Typography>} />
                            </ListItem>

                            {/* Point 46 */}
                            <ListItem sx={{ pl: 0 }}>
                                <ListItemText primary={<Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>CAB shall not offer inducement of any kind such as any gifts or any kind of payments in cash or any undue favour to the assessment team members. In case of any violation, NABL shall initiate adverse action as per NABL 216.</Typography>} />
                            </ListItem>

                            {/* Point 47 */}
                            <ListItem sx={{ pl: 0 }}>
                                <ListItemText primary={<Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>All information obtained by NABL and its personnel in NABL's direct dealing with the accredited CABs in all stages from application onwards, other than the information in public domain shall be treated as confidential. When NABL is required by law or authorized by contractual arrangements to release the confidential information, the CAB shall be notified unless prohibited by law.</Typography>} />
                            </ListItem>

                            {/* Point 48 */}
                            <ListItem sx={{ pl: 0 }}>
                                <ListItemText primary={<Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>CABs acknowledges and agrees to maintain strict confidentiality with regard to all sensitive information, records, data, and proprietary materials obtained or accessed during the accreditation process. CABs shall not disclose, share, or disseminate any confidential information obtained from NABL or any other CABs, clients, or stakeholders participating in the accreditation process.</Typography>} />
                            </ListItem>

                            {/* Point 49 */}
                            <ListItem sx={{ pl: 0 }}>
                                <ListItemText primary={<Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>If the CAB engages subcontractors, consultants, or third-party service providers to assist in the accreditation process, the CAB shall declare these to NABL and ensure that such entities/individuals also comply with the same level of confidentiality and other obligations as set forth herein.</Typography>} />
                            </ListItem>
                            {/* Point 50 */}
                            <ListItem sx={{ pl: 0 }}>
                                <ListItemText
                                    primary={
                                        <Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>
                                            The CAB shall implement appropriate measures to protect the confidentiality and
                                            security of any personal data or sensitive information received or processed during
                                            the accreditation process. CAB shall ensure full compliance with the provisions of
                                            the Digital Personal Data Protection Act, 2023, and rules made thereunder,
                                            including obligations relating to lawful processing, consent, purpose limitation,
                                            data security, data retention, and data principal rights.
                                        </Typography>
                                    }
                                />
                            </ListItem>

                            {/* Point 51 */}
                            <ListItem sx={{ pl: 0 }}>
                                <ListItemText primary={<Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>The CAB acknowledges that any breach of the confidentiality obligations outlined in this clause may result in the termination of the accreditation process, and may also give rise to legal liabilities.</Typography>} />
                            </ListItem>

                            {/* Point 52 */}
                            <ListItem sx={{ pl: 0 }}>
                                <ListItemText primary={<Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>The confidentiality obligations outlined in this clause shall continue to apply even after the conclusion of the accreditation process, regardless of whether accreditation is granted or not, except as required by law.</Typography>} />
                            </ListItem>

                            {/* Point 53 */}
                            <ListItem sx={{ pl: 0 }}>
                                <ListItemText primary={<Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>Information about the conformity assessment body obtained from sources other than the conformity assessment body (e.g. complainant, regulators) shall be confidential between the conformity assessment body and NABL. The provider of this information is confidential to NABL and shall not be shared with the conformity assessment body, unless agreed by the source.</Typography>} />
                            </ListItem>

                            {/* Point 54 */}
                            <ListItem sx={{ pl: 0 }}>
                                <ListItemText primary={<Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>The CAB shall indemnify and hold NABL, its CEO, officers, directors, employees, and representatives protected against any claims, demands, losses, damages, penalties, costs, or legal proceedings arising out of or in connection with any non-compliance by the CAB with applicable laws, rules or regulations including the Digital Personal Data Protection Act, 2023, or any act, omission, negligence in the conduct of testing, calibration, sampling, inspection, certification related laboratory activities or associated services or misuse, misrepresentation or unauthorised reference to NABL accreditation or any activity prejudicial to public interest, public health or national interest.</Typography>} />
                            </ListItem>

                            {/* Point 55 */}
                            <ListItem sx={{ pl: 0 }}>
                                <ListItemText primary={<Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>The CAB agrees that NABL shall not be liable for any damages, losses, or expenses incurred by the CAB due to the rejection or withdrawal of its accreditation application or accreditation status, regardless of the reasons for such rejection or withdrawal.</Typography>} />
                            </ListItem>

                            {/* Point 56 */}
                            <ListItem sx={{ pl: 0 }}>
                                <ListItemText primary={<Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>NABL shall not be liable for any failure or delay in performing its obligations under the accreditation process due to force majeure events or circumstances beyond its reasonable control.</Typography>} />
                            </ListItem>

                            {/* Point 57 */}
                            <ListItem sx={{ pl: 0 }}>
                                <ListItemText primary={<Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>The CAB shall maintain adequate and appropriate insurance coverage to protect itself against any liabilities, claims, or damages that may arise during the accreditation process.</Typography>} />
                            </ListItem>

                            {/* Point 58 */}
                            <ListItem sx={{ pl: 0 }}>
                                <ListItemText primary={<Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>The CAB seeking accreditation hereby agrees to abide by all applicable rules, regulations, guidelines, and standards set forth by NABL and other relevant authorities throughout the accreditation process and the provision of conformity assessment services.</Typography>} />
                            </ListItem>

                            {/* Point 59 */}
                            <ListItem sx={{ pl: 0 }}>
                                <ListItemText primary={<Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>The CAB shall ensure strict compliance with the accreditation criteria, policies, procedures, and guidelines issued by NABL. The CAB shall implement any necessary corrective actions to address any non-conformities identified during assessments or audits.</Typography>} />
                            </ListItem>

                            {/* Point 60 */}
                            <ListItem sx={{ pl: 0 }}>
                                <ListItemText primary={<Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>After exhausting all remedies provided by NABL to resolve any issue, the CAB may approach courts at New Delhi and none other and shall be governed by the laws of India.</Typography>} />
                            </ListItem>

                            {/* Point 61 */}
                            <ListItem sx={{ pl: 0 }}>
                                <ListItemText
                                    primary={
                                        <Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>
                                            If the acts of the CAB are violative of NABL policy, rules, regulations or the CAB
                                            is found indulged in fraudulent and unethical activities or any such activities
                                            adversely affecting the integrity of NABL or violating accreditation terms, then
                                            NABL shall be at liberty to take appropriate action as per its policy, rules and
                                            regulations which may extend to removal of CABs from the NABL website. In case a
                                            CAB has approached the court against the decision or action of NABL, the
                                            accreditation certificate (related to the legal entity), and if there are multiple
                                            CABs accredited by NABL at different locations belonging to the same legal entity,
                                            then each one of the accredited CABs shall remain removed from the NABL website.
                                            Existing activities of all CABs linked to that legal entity shall be put on hold
                                            till pendency or resolution of the matter depending on gravity. CAB shall not
                                            submit any application during pendency of court proceedings and any such
                                            application shall not be entertained by NABL.
                                        </Typography>
                                    }
                                />
                            </ListItem>

                            {/* Point 62 */}
                            <ListItem sx={{ pl: 0 }}>
                                <ListItemText primary={<Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>The CAB acknowledges and agrees that NABL shall not be held responsible or liable, directly or indirectly, for the technical competence, results, reports, opinions, decisions, services, business practices, contractual arrangements or any acts or omissions of the laboratory and/or its clients. NABL accreditation shall not be represented, used or construed as an endorsement, approval, supervision, guarantee or assumption of responsibility by NABL.</Typography>} />
                            </ListItem>

                            {/* Point 63 */}
                            <ListItem sx={{ pl: 0 }}>
                                <ListItemText primary={<Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>In the event that a CAB is found or is reasonably suspected to have engaged in activities contrary to applicable law, NABL regulations, accreditation criteria, public interest, public health, national interest or data protection obligations, NABL shall be entitled to initiate appropriate action including but not limited to suspension, reduction of scope, withdrawal of accreditation or debarment in accordance with NABL 216 and other applicable NABL documents.</Typography>} />
                            </ListItem>

                            {/* Point 64 */}
                            <ListItem sx={{ pl: 0 }}>
                                <ListItemText primary={<Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>Without prejudice to the aforesaid rights and remedies, NABL shall reserve the right to initiate appropriate contractual or legal action as may be necessary to enforce the terms of the accreditation agreement where the acts or omissions of the CAB constitute breach of contractual obligations, fraudulent activities, unethical practices, negligence, or misconduct causing prejudice to NABL’s legal position, reputation, or the integrity and credibility of the accreditation system.</Typography>} />
                            </ListItem>

                            {/* Point 65 */}
                            <ListItem sx={{ pl: 0 }}>
                                <ListItemText primary={<Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>Repeat violation of terms & conditions and commitments given by CAB to NABL shall be dealt strictly as per NABL 216 and in accordance with the applicable law.</Typography>} />
                            </ListItem>

                            {/* Point 66 */}
                            <ListItem sx={{ pl: 0 }}>
                                <ListItemText primary={<Typography variant="body1" sx={{ textAlign: "justify", lineHeight: 1.6 }}>The existing CAB owner(s) shall be responsible for all the related compliances and violations (if any) for the previous and current CAB application(s) processed by NABL.</Typography>} />
                            </ListItem>
                        </List>

                        <Typography variant="body1" paragraph sx={{ textAlign: 'justify', lineHeight: 1.6 }}>
                            By accepting these terms and conditions, it is implied that a CAB as an applicant and after obtaining accreditation agrees to comply at all times with all Terms and Conditions for Obtaining and Maintaining NABL Accreditation. Any violation of this terms and conditions (arrangement) shall result in adverse decision as per NABL 216 'Procedures for Dealing with Adverse Decisions' including legal action.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ textAlign: 'justify', lineHeight: 1.6, fontWeight: 'bold', color: 'error.main' }}>
                            Dispute, if any, arising out of NABL decisions that remain unresolved through mechanism provided by NABL are subject to the exclusive jurisdiction of the Courts at New Delhi and none other.
                        </Typography>
                    </CardContent>
                </Card>

                {/* Declaration by the Laboratory */}
                <Card elevation={2} sx={{ mb: 4 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                            <AssignmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                            Declaration by the Laboratory
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ fontWeight: 'bold', mb: 3 }}>
                            We declare that:
                        </Typography>

                        <List sx={{ pl: 2 }}>
                            <ListItem sx={{ display: 'list-item', listStyleType: 'disc', pl: 0 }}>
                                <ListItemText>
                                    <Typography variant="body1" sx={{ textAlign: 'justify', lineHeight: 1.6 }}>
                                        We are familiar with the Terms & Conditions for Obtaining and Maintaining NABL Accreditation (NABL 131), which is enclosed and will abide by it. We have understood the requirements given in NABL 133.
                                    </Typography>
                                </ListItemText>
                            </ListItem>

                            <ListItem sx={{ display: 'list-item', listStyleType: 'disc', pl: 0 }}>
                                <ListItemText>
                                    <Typography variant="body1" sx={{ textAlign: 'justify', lineHeight: 1.6 }}>
                                        We will maintain strict confidentiality of personal sensitive data. This information will not be shared with any other person, agency or organization (unless required by law).
                                    </Typography>
                                </ListItemText>
                            </ListItem>

                            <ListItem sx={{ display: 'list-item', listStyleType: 'disc', pl: 0 }}>
                                <ListItemText>
                                    <Typography variant="body1" sx={{ textAlign: 'justify', lineHeight: 1.6 }}>
                                        We have declared all sources of samples.
                                    </Typography>
                                </ListItemText>
                            </ListItem>

                            <ListItem sx={{ display: 'list-item', listStyleType: 'disc', pl: 0 }}>
                                <ListItemText>
                                    <Typography variant="body1" sx={{ textAlign: 'justify', lineHeight: 1.6 }}>
                                        We have declared and submitted accurate and complete information in the enclosed "Declaration Form"
                                    </Typography>
                                </ListItemText>
                            </ListItem>

                            <ListItem sx={{ display: 'list-item', listStyleType: 'disc', pl: 0 }}>
                                <ListItemText>
                                    <Typography variant="body1" sx={{ textAlign: 'justify', lineHeight: 1.6 }}>
                                        We have conducted internal audit of our Sample Collection Centre(s)/ facility(ies) at least once during the last one year.
                                    </Typography>
                                </ListItemText>
                            </ListItem>

                            <ListItem sx={{ display: 'list-item', listStyleType: 'disc', pl: 0 }}>
                                <ListItemText>
                                    <Typography variant="body1" sx={{ textAlign: 'justify', lineHeight: 1.6 }}>
                                        We agree to comply fully with ISO 15189: 2012 or ISO 15189: 2022 and relevant specific criteria for the accreditation of testing laboratory and associated Sample Collection Centre(s)/facility(ies).
                                    </Typography>
                                </ListItemText>
                            </ListItem>

                            <ListItem sx={{ display: 'list-item', listStyleType: 'disc', pl: 0 }}>
                                <ListItemText>
                                    <Typography variant="body1" sx={{ textAlign: 'justify', lineHeight: 1.6 }}>
                                        We agree to comply with accreditation procedures, pay all costs for pre-assessment (if applicable), assessment, supplementary visit (if any), surveillance and reassessment irrespective of the result.
                                    </Typography>
                                </ListItemText>
                            </ListItem>

                            <ListItem sx={{ display: 'list-item', listStyleType: 'disc', pl: 0 }}>
                                <ListItemText>
                                    <Typography variant="body1" sx={{ textAlign: 'justify', lineHeight: 1.6 }}>
                                        We agree to co-operate with the assessment team appointed by NABL for examination of all relevant documents/records by them and their visits to those parts of the laboratory that are part of the scope of accreditation.
                                    </Typography>
                                </ListItemText>
                            </ListItem>

                            <ListItem sx={{ display: 'list-item', listStyleType: 'disc', pl: 0 }}>
                                <ListItemText>
                                    <Typography variant="body1" sx={{ textAlign: 'justify', lineHeight: 1.6 }}>
                                        We comply to all national, regional and local regulatory requirements for operating a laboratory.
                                    </Typography>
                                </ListItemText>
                            </ListItem>

                            <ListItem sx={{ display: 'list-item', listStyleType: 'disc', pl: 0 }}>
                                <ListItemText>
                                    <Typography variant="body1" sx={{ textAlign: 'justify', lineHeight: 1.6 }}>
                                        All information provided in this application is true.
                                    </Typography>
                                </ListItemText>
                            </ListItem>
                        </List>

                        <Typography variant="body1" paragraph sx={{ mt: 2, fontStyle: 'italic' }}>
                            Self-declaration shall be submitted by the laboratory on the basis of the internal audit conducted by them, to confirm to NABL that their Sample Collection Centre(s)/ facility(ies) are complying with NABL norms and relevant clauses of ISO 15189.
                        </Typography>
                    </CardContent>
                </Card>

                {/* Authorized Representative */}
                <Card elevation={2} sx={{ mb: 4 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                            <SecurityIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                            Authorized Representative
                        </Typography>

                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Representative Name"
                                    value={declarationData.representativeName}
                                    onChange={handleTextFieldChange('representativeName')}
                                    variant="outlined"
                                    required
                                    disabled={fieldsDisabled}
                                    error={!fieldsDisabled && !declarationData.representativeName?.trim()}
                                    helperText={!fieldsDisabled && !declarationData.representativeName?.trim() ? 'Representative name is required' : ''}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Representative Title"
                                    value={declarationData.representativeTitle}
                                    onChange={handleTextFieldChange('representativeTitle')}
                                    variant="outlined"
                                    required
                                    disabled={fieldsDisabled}
                                    error={!fieldsDisabled && !declarationData.representativeTitle?.trim()}
                                    helperText={!fieldsDisabled && !declarationData.representativeTitle?.trim() ? 'Representative title is required' : ''}
                                />
                            </Grid>
                        </Grid>

                        <Typography variant="body1" paragraph sx={{ mt: 2, textAlign: 'justify' }}>
                            <strong>{declarationData.representativeName}</strong>, <strong>{declarationData.representativeTitle}</strong>,
                            authorised representative of CAB has filled/ verified the contents of the application and accepted the above mentioned terms and conditions on behalf of CAB.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ fontWeight: 'bold' }}>
                            All information provided in this application are true.
                        </Typography>
                    </CardContent>
                </Card>

                {/* File Upload Section */}
                <Card elevation={2} sx={{ mb: 4 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                            <DescriptionIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                            Upload Declaration by the Laboratory Shareholders and Directors *
                        </Typography>

                        <Alert severity={declarationData.shareholderDirectorFile ? 'success' : 'warning'} sx={{ mb: 2 }}>
                            {declarationData.shareholderDirectorFile
                                ? 'Declaration file uploaded successfully.'
                                : 'Please upload the declaration signed by laboratory shareholders and directors. This is required.'
                            }
                        </Alert>

                        <FileUploadComponent
                            fieldName="shareholderDirectorFile"
                            label="Declaration by Shareholders and Directors"
                            orgId={getOrgId()}
                            section="declaration-documents"
                            required={true}
                            accept=".pdf,.jpg,.jpeg,.png"
                            maxSize={10}
                            multiple={false}
                            onChange={handleFileUpload}
                            initialFile={declarationData.shareholderDirectorFile}
                            disabled={fieldsDisabled}
                        />

                        <Box sx={{ mt: 2 }}>
                            <Button
                                variant="outlined"
                                startIcon={<DownloadIcon />}
                                size="small"
                                onClick={() => {
                                    // Download the Declaration Form from public folder
                                    const link = document.createElement('a');
                                    link.href = `${process.env.PUBLIC_URL}/Declaration Form.docx`;
                                    link.download = 'Declaration Form.docx';
                                    link.target = '_blank';
                                    document.body.appendChild(link);
                                    link.click();
                                    document.body.removeChild(link);
                                    showNotification('Downloading Declaration Form...', 'info');
                                }}
                            >
                                Download Format
                            </Button>
                        </Box>
                    </CardContent>
                </Card>

                {/* Final Declaration */}
                <Card elevation={2} sx={{
                    mb: 4,
                    backgroundColor: declarationData.finalDeclarationAgreed ? 'success.50' : 'warning.50',
                    borderLeft: '5px solid',
                    borderLeftColor: declarationData.finalDeclarationAgreed ? 'success.main' : 'warning.main'
                }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom sx={{
                            color: declarationData.finalDeclarationAgreed ? 'success.dark' : 'warning.dark',
                            fontWeight: 'bold'
                        }}>
                            <WarningIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                            Final Agreement *
                        </Typography>

                        {!declarationData.finalDeclarationAgreed && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                You must agree to the terms and conditions to proceed with saving the declaration.
                            </Alert>
                        )}

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={declarationData.termchk}
                                    onChange={handleCheckboxChange('termchk')}
                                    color={declarationData.termchk ? "success" : "warning"}
                                    required
                                    disabled={fieldsDisabled}
                                />
                            }
                            label="I have read and understood all the terms and conditions mentioned above."
                            sx={{ alignItems: 'flex-start', mb: 2 }}
                        />

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={declarationData.finalDeclarationAgreed}
                                    onChange={handleCheckboxChange('finalDeclarationAgreed')}
                                    color={declarationData.finalDeclarationAgreed ? "success" : "warning"}
                                    required
                                    disabled={fieldsDisabled}
                                />
                            }
                            label="By accepting the above terms & conditions, it is implied that a CAB/ Sample Collection Centre/ Facility (ies) declared by medical CAB as an applicant and after obtaining accreditation agrees to comply at all times with all Terms and Conditions for NABL Accreditation. Any violation of this terms and conditions shall result in adverse decision such as abeyance, suspension, forced withdrawal etc against the CAB as specified in 'NABL 216'."
                            sx={{ alignItems: 'flex-start' }}
                        />
                    </CardContent>
                </Card>




                {/* Action Buttons - Simplified Logic */}
                {(applicationStage === null || applicationStage === "18a") && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>


                        {/* Save Declaration Button - Show when fields are not complete or not saved yet */}
                        {showSaveButton && (
                            <Button
                                variant="contained"
                                startIcon={<SaveIcon />}
                                onClick={handleSaveDeclaration}
                                disabled={loading || !canSaveDeclaration()}
                                size="large"
                                color="primary"
                                sx={{
                                    background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                                    boxShadow: '0 3px 5px 2px rgba(33, 203, 243, .3)',
                                }}
                            >
                                {loading ? 'Saving...' : 'Save Declaration'}
                            </Button>
                        )}

                        {/* Payment Buttons - Show when payment failed or no payment made yet */}
                        {showPaymentButtons && (
                            <>
                                <Button
                                    variant="outlined"
                                    startIcon={<ViewIcon />}
                                    onClick={handlePreviewReport}
                                    disabled={loading}
                                    size="large"
                                    color="secondary"
                                    sx={{
                                        borderColor: 'secondary.main',
                                        '&:hover': {
                                            borderColor: 'secondary.dark',
                                            backgroundColor: 'secondary.50'
                                        }
                                    }}
                                >
                                    Preview Report
                                </Button>

                                <Button
                                    variant="contained"
                                    startIcon={<PaymentIcon />}
                                    onClick={handleProceedToPay}
                                    disabled={loading}
                                    size="large"
                                    color="success"
                                    sx={{
                                        background: 'linear-gradient(45deg, #4CAF50 30%, #8BC34A 90%)',
                                        boxShadow: '0 3px 5px 2px rgba(76, 175, 80, .3)',
                                    }}
                                >
                                    Proceed to Pay
                                </Button>
                            </>
                        )}

                        {/* Submit Final Declaration Button - Show when payment is successful */}
                        {showSubmitButton && (
                            <Button
                                variant="contained"
                                startIcon={<SendIcon />}
                                onClick={() => setSubmitDialog(true)}
                                disabled={loading}
                                size="large"
                                color="success"
                                sx={{
                                    background: 'linear-gradient(45deg, #FF6B6B 30%, #4ECDC4 90%)',
                                    boxShadow: '0 3px 5px 2px rgba(76, 175, 80, .3)',
                                }}
                            >
                                Final Submit
                            </Button>
                        )}

                    </Box>
                )}

            </Paper>

            {/* Password Verification Dialog */}
            <Dialog open={passwordDialog} onClose={handlePasswordDialogClose} maxWidth="sm" fullWidth>
                <DialogTitle sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    backgroundColor: 'primary.main',
                    color: 'white'
                }}>
                    <LockIcon />
                    Payment Authorization Required
                </DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                        Please enter the authorization password to proceed with payment:
                    </Typography>
                    <TextField
                        fullWidth
                        type="password"
                        label="Login Password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        error={!!passwordError}
                        helperText={passwordError || 'Enter your login password to authorize payment'}
                        variant="outlined"
                        disabled={loading}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter' && !loading) {
                                handlePasswordVerification();
                            }
                        }}
                        autoFocus
                    />
                    <Alert severity="info" sx={{ mt: 2 }}>
                        <Typography variant="body2">
                            Enter the same password you use to login to your account.
                            This ensures secure access to the payment gateway.
                        </Typography>
                    </Alert>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button
                        onClick={handlePasswordDialogClose}
                        color="inherit"
                        disabled={loading}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handlePasswordVerification}
                        variant="contained"
                        disabled={!password.trim() || loading}
                    >
                        {loading ? 'Verifying...' : 'Verify & Proceed'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Submit Confirmation Dialog */}
            <Dialog open={submitDialog} onClose={() => setSubmitDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Submit Final Declaration</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Are you sure you want to submit the final declaration? This action confirms your agreement to all terms and conditions.
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Once submitted, you will not be able to modify the declaration.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSubmitDialog(false)} disabled={loading}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmitDeclaration}
                        variant="contained"
                        color="success"
                        disabled={loading}
                    >
                        {loading ? 'Submitting...' : 'Submit Declaration'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Notification Snackbar */}
            <Snackbar
                open={notification.open}
                autoHideDuration={6000}
                onClose={closeNotification}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert
                    onClose={closeNotification}
                    severity={notification.severity}
                    sx={{ width: '100%' }}
                >
                    {notification.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default DeclarationSection;